import React from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { StylishNewChip } from 'components/DesignSystems/New/StylishNewChip';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import { useGetUserSchedule } from 'hooks/incident/useSchedule';
import { useResourceRelationships } from 'hooks/resources/useResourceRelationships';
import { useResourcesInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import { CHECKED_IN_STATUSES } from 'components/IAP/Resources/constants/resourceConstants';
import MemberResourceAssignment from './MemberResourceAssignment';
import MemberActivationStatus from './MemberActivationStatus';
import { HwcgActivationUserTimeline } from './activationLog/HwcgActivationUserTimeline';
import { useHwcgActivationLog } from 'hooks/useHwcgActivationLog';
import { useHwcgFlag } from 'utils/featureFlags';
import { RosterExpandedRowProps, Schedule } from './types';
import { selectUserGuid, selectGroupGuid } from 'slices/commonSelectors';
import {
  useGetIncidentLogs,
  useCreateIncidentMemberSession,
  useUpdateIncidentMemberSession,
  IncidentLogEntry,
  WorkLog,
  Session,
} from 'hooks/incident/useWorkLog';
import { useActualIncidentId } from '../../IAP/hooks/commonHooks';

const numToDay: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
};

const RosterExpandedRow: React.FC<RosterExpandedRowProps> = ({
  row,
  onDeleteSchedule,
  onToggleButtonClick,
  isRosterLoading,
  rosterData,
  removeMemberButtonClicked,
  removeSelfFromIncidentButtonClicked,
  editMemberButtonClicked,
  toggleDataCheckIn,
  toggleDataSignIn,
  simplified = false,
  currentUserHasApprovalAuthority,
  onToggleApprovalAuthority,
  isTogglingAuthority,
  showApprovalAuthority = true,
  tourHost = false,
}) => {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  const groupGuid = useSelector(selectGroupGuid);
  const hwcg = useHwcgFlag();
  const { data: activationLog } = useHwcgActivationLog(incidentId, {
    enabled: hwcg && !simplified,
  });
  const activationUser = hwcg
    ? activationLog?.users.find((user) => user.userGuid === row.userId)
    : undefined;

  const { data: userSchedule } = useGetUserSchedule(
    incidentId,
    groupGuid || '',
    row.userId,
    row.incidentMemberId
  );

  const { data: memberResources = [] } = useResourceRelationships(
    row.incidentMemberId,
    'member'
  );

  const { data: resourcesInIncident = [] } = useResourcesInIncident();

  const { data: incidentLogs } = useGetIncidentLogs();
  const createSession = useCreateIncidentMemberSession();
  const updateSession = useUpdateIncidentMemberSession();

  const hasCheckedInResources = React.useMemo(() => {
    if (!memberResources || memberResources.length === 0) return false;

    const memberResourceSetIds = memberResources.map((r) => r.id);
    return resourcesInIncident.some(
      (resource) =>
        memberResourceSetIds.includes(resource.resource_set_id) &&
        CHECKED_IN_STATUSES.some((status) => status === resource.current_status)
    );
  }, [memberResources, resourcesInIncident]);

  const isUserCheckedIn = !!(row.active && row.activeTime);
  const isUserActivated = row.activatedTimestamp;

  const userLog = React.useMemo<IncidentLogEntry | undefined>(() => {
    return incidentLogs?.find(
      (log: IncidentLogEntry) => log.userGuid === row.userId
    );
  }, [incidentLogs, row.userId]);

  const workLogId = React.useMemo(() => {
    if (!userLog?.logs || userLog.logs.length === 0) return null;
    return userLog.logs[0].workLogId || null;
  }, [userLog]);

  const activeSession = React.useMemo(() => {
    if (!userLog?.logs) return null;
    const allSessions = userLog.logs.flatMap(
      (log: WorkLog) => log.sessions || []
    );
    return allSessions.find(
      (session: Session) => session.signInTime && !session.signOutTime
    );
  }, [userLog]);

  const isSignedIn = !!activeSession;

  const handleSignInToggle = (value: string) => {
    if (value === 'Signed-In' && !isSignedIn) {
      handleSignIn();
    } else if (value === 'Signed-Out' && isSignedIn) {
      handleSignOut();
    }
  };

  const handleSignIn = () => {
    if (!workLogId) {
      console.error('No workLogId found for sign in action');
      return;
    }
    createSession.mutate({
      incidentId,
      userGuid: row.userId,
      workLogId,
      signInTime: new Date().toISOString(),
    });
  };

  const handleSignOut = () => {
    if (!activeSession || !workLogId) {
      return;
    }
    updateSession.mutate({
      incidentId,
      userGuid: row.userId,
      workLogId,
      sessionId: activeSession.sessionId,
      signInTime: activeSession.signInTime,
      signOutTime: new Date().toISOString(),
    });
  };

  return (
    <>
      <div
        className="row"
        data-tour={tourHost ? 'roster-member-detail' : undefined}
      >
        <div className="col-md-6">
          <div className="fw-bold my-2">Profile</div>
          <span className="d-block">Email: {row.emailAddress}</span>
          <span className="d-block">
            Phone (Mobile): {row.contact?.mobilePhone}
          </span>
          <span className="d-block">
            Phone (Home): {row.contact?.homePhone}
          </span>
          <span className="d-block">Pager: {row.contact?.pager}</span>
        </div>
        <div className="col-md-6">
          <div className="d-flex align-items-center justify-content-left">
            <div className="fw-bold my-2">Seats</div>
            {!simplified ? (
              <button
                className="button button--link ms-2 ps-0"
                data-tour={tourHost ? 'roster-edit-seats' : undefined}
                onClick={() => editMemberButtonClicked(row)}
              >
                Edit Seats
              </button>
            ) : null}
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column align-items-left">
              {row?.incidentPositions.map((position) => (
                <div className="me-3" key={position.positionId}>
                  {position.positionName}
                </div>
              ))}
              {/* TODO: Apply when permissions ready for front-end
              hasPermission(reduxValidateRBACPermissionForActionResult, 'incident:member:add') && ( */}
            </div>
            {showApprovalAuthority && !simplified && (
              <div className="d-flex flex-column align-items-end">
                <div className="d-flex align-items-center">
                  <div className="form-check form-switch me-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`approval-authority-${row.incidentMemberId}`}
                      checked={row.approvalAuthority || false}
                      onChange={() => {
                        if (
                          row.userId !== userGuid &&
                          onToggleApprovalAuthority
                        ) {
                          onToggleApprovalAuthority(row);
                        }
                      }}
                      disabled={
                        isTogglingAuthority ||
                        row.userId === userGuid ||
                        !currentUserHasApprovalAuthority
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`approval-authority-${row.incidentMemberId}`}
                    >
                      Approval Authority
                    </label>
                  </div>
                </div>
                {row.userId === userGuid && row.approvalAuthority && (
                  <small className="text-muted d-block mt-1">
                    You cannot remove your own approval authority
                  </small>
                )}
              </div>
            )}
          </div>
        </div>
        {row.relevantTeamMemberships &&
          Array.isArray(row.relevantTeamMemberships) &&
          !!row.relevantTeamMemberships.length && (
            <>
              <div className="fw-bold my-2">Relevant Team Memberships</div>
              {row.relevantTeamMemberships.map((team, idx) => (
                <div key={`membership-${idx}`}>{team.name}</div>
              ))}
            </>
          )}
      </div>

      {/* Resource Assignment Section */}
      {!simplified ? (
        <>
          <MemberResourceAssignment
            incidentMemberId={row.incidentMemberId}
            incidentId={incidentId}
            isUserCheckedIn={isUserCheckedIn}
            hasCheckedInResources={hasCheckedInResources}
            userGuid={userGuid}
          />

          {/* Schedule Display */}
          {userSchedule && userSchedule.length > 0 && (
            <div className="form-chip-group mt-4">
              {userSchedule?.map((schedule: Schedule) => {
                const schedules = schedule.allday ? (
                  <StylishNewChip
                    key={schedule.id}
                    label={`${dayjs(schedule.start_time).format('MM/DD/YYYY')} - ${dayjs(
                      schedule.end_time
                    ).format('MM/DD/YYYY')}, Full Day`}
                    handleDelete={() => onDeleteSchedule(schedule.id, row)}
                  />
                ) : schedule.isrecurringevent ? (
                  <StylishNewChip
                    key={schedule.id}
                    label={`From ${dayjs(schedule.start_time).format('MM/DD/YYYY')} to ${dayjs(
                      schedule.end_time
                    ).format('MM/DD/YYYY')} Every \n ${schedule.daysofweek
                      ?.map((key: number) => numToDay[key])
                      .join(', ')} From ${schedule.recurringstarttime} to ${
                      schedule.recurringendtime
                    }`}
                    handleDelete={() => onDeleteSchedule(schedule.id, row)}
                  />
                ) : (
                  <StylishNewChip
                    key={schedule.id}
                    label={`${dayjs(schedule.start_time).format('MM/DD/YYYY HH:mm')} - ${dayjs(
                      schedule.end_time
                    ).format('MM/DD/YYYY HH:mm')}`}
                    handleDelete={() => onDeleteSchedule(schedule.id, row)}
                  />
                );
                return schedules;
              })}
            </div>
          )}

          {/* Activation Status Section */}
          <MemberActivationStatus
            row={row}
            incidentId={incidentId}
            simplified={simplified}
            currentUserHasApprovalAuthority={currentUserHasApprovalAuthority}
          />

          {hwcg && activationLog ? (
            <div className="dark tw:mt-3 tw:rounded-md tw:border tw:border-neutral-700 tw:p-3">
              <div className="fw-bold mb-2">Activation notices</div>
              <HwcgActivationUserTimeline
                user={activationUser}
                showHeader={false}
              />
            </div>
          ) : null}

          {/* Action Buttons */}
          <div className="button-group d-block d-lg-flex mt-4">
            {/* Show toggle for check in/out if user has no resources */}
            {/* TODO: Apply when permissions ready for front-end
        {(!memberResources || memberResources.length === 0) &&
          (row.user_guid === userGuid ||
            !!reduxValidateRBACPermissionForActionResult['incident:member:add']) && ( */}
            {(!memberResources || memberResources.length === 0) && (
              <div>
                <StylishNewToggleButtonGroup
                  data={toggleDataCheckIn}
                  selected={isUserCheckedIn ? 'Checked-In' : 'Checked Out'}
                  onChange={(value) => {
                    // Prevent checking out when signed in
                    if (
                      (value === 'Checked Out' || value === 'Checked-Out') &&
                      isSignedIn
                    ) {
                      toast.error('You must sign out before checking out');
                      return;
                    }
                    onToggleButtonClick(value, row);
                  }}
                  className="d-md-inline-flex me-4"
                  disabled={isRosterLoading || !isUserActivated}
                  onBlur={() => {}}
                />
                <StylishNewToggleButtonGroup
                  data={toggleDataSignIn}
                  selected={isSignedIn ? 'Signed-In' : 'Signed-Out'}
                  onChange={handleSignInToggle}
                  className="d-md-inline-flex"
                  disabled={
                    isRosterLoading ||
                    createSession.isPending ||
                    updateSession.isPending ||
                    !isUserCheckedIn ||
                    !isUserActivated
                  }
                  onBlur={() => {}}
                />
              </div>
            )}

            {!simplified ? (
              <div className="button-group mt-xl-0 ms-auto mt-4">
                {rosterData?.length > 1 || tourHost ? (
                  <>
                    {row.userId !== userGuid && rosterData?.length > 1 ? (
                      <StylishNewButton
                        className="button--tertiary button--reverse"
                        data-tour={
                          tourHost ? 'roster-remove-member' : undefined
                        }
                        onClick={() => removeMemberButtonClicked(row)}
                      >
                        Remove Member
                      </StylishNewButton>
                    ) : null}
                    {row.userId === userGuid ? (
                      <StylishNewButton
                        className="button--tertiary button--reverse"
                        data-tour={
                          tourHost ? 'roster-remove-member' : undefined
                        }
                        onClick={() => removeSelfFromIncidentButtonClicked(row)}
                      >
                        Leave incident
                      </StylishNewButton>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
      {simplified ? (
        <div className="button-group mt-4">
          {row.userId !== userGuid ? (
            <StylishNewButton
              className="button--tertiary button--reverse"
              onClick={() => removeMemberButtonClicked(row)}
            >
              Remove Member
            </StylishNewButton>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default RosterExpandedRow;
